import React from "react";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import { dateTimeHelper } from "helper/dateTime";
import { formatHelper } from "helper/format";

const STATUS_INFO = {
  ACTIVATED: {
    color: "#00C565",
    describe: (billingDate) =>
      billingDate
        ? `Next billing date: ${dateTimeHelper.displayDate(billingDate)}`
        : "Your plan is currently active.",
  },
  EXPIRED: {
    color: "#d44c4c",
    describe: (billingDate) =>
      billingDate
        ? `Your subscription has expired on ${dateTimeHelper.displayDate(billingDate)}`
        : "Your subscription has expired.",
  },
  CANCELLED: {
    color: "#df873a",
    describe: (billingDate) =>
      billingDate
        ? `Your subscription was cancelled on ${dateTimeHelper.displayDate(billingDate)}`
        : "Your subscription was cancelled.",
  },
  PAUSED: {
    color: "#df873a",
    describe: (billingDate) =>
      billingDate
        ? `Your subscription was paused on ${dateTimeHelper.displayDate(billingDate)}`
        : "Your subscription is paused.",
  },
};

const extractTier = (planName) => {
  const match = /\(([^)]+)\)/.exec(planName || "");
  if (match) return match[1];

  const tierMatch = /\b(Premium|Plus|Basic|Standard|Pro)\b/i.exec(planName || "");
  if (tierMatch) {
    const tier = tierMatch[1];
    return `${tier.charAt(0).toUpperCase()}${tier.slice(1).toLowerCase()}`;
  }

  return null;
};

/**
 * Figma 7537:57629 — "Active Plan Detailed" (e.g. "Jarvis Portfolio
 * (Premium)"): icon + name + billing subtitle, price on the right,
 * footer description + "Manage Subscription" link.
 */
const PlanDetailCard = ({
  icon,
  iconWidth = 70,
  iconHeight = 72,
  name,
  planStatus,
  billingDate,
  price,
  frequency,
  onManageClick,
  investMore,
}) => {
  const status = STATUS_INFO[planStatus] || STATUS_INFO.ACTIVATED;
  const tier = extractTier(name);
  const isActive = planStatus === "ACTIVATED" || !planStatus;

  const footerText = isActive
    ? tier
      ? (
        <>
          You are currently on the <b>{tier} Tier</b>. Upgrading will take
          effect immediately.
        </>
      )
      : "You are currently on this plan. Upgrading will take effect immediately."
    : status.describe(billingDate);

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: "20px",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.02)",
        p: 4,
      }}
    >
      <Stack spacing={1.5} sx={{ width: "100%" }}>
        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack spacing={1.5} alignItems="flex-start">
            {icon ? (
              <Box
                component="img"
                src={icon}
                alt=""
                sx={{
                  width: iconWidth,
                  height: iconHeight,
                  objectFit: "contain",
                  borderRadius: "8px",
                  filter: "drop-shadow(0px 4px 14px rgba(0,0,0,0.25))",
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Stack spacing={0.5}>
              <Text
                sx={{ fontWeight: 700, fontSize: "20px", color: "#111827" }}
              >
                {name}
              </Text>
              {!isActive ? (
                <Text
                  sx={{
                    fontSize: { xs: "12px", md: "14px" },
                    fontWeight: 600,
                    color: status.color,
                  }}
                >
                  {status.describe(billingDate)}
                </Text>
              ) : billingDate ? (
                <Text sx={{ fontSize: { xs: "12px", md: "14px" }, color: "#4B5563" }}>
                  {status.describe(billingDate)}
                </Text>
              ) : null}
            </Stack>
          </Stack>

          {price != null ? (
            <Stack
              direction="row"
              alignItems="baseline"
              spacing={0.5}
              sx={{ flexShrink: 0 }}
            >
              <Text
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "20px", md: "32px" },
                  color: "#111827",
                }}
              >
                {formatHelper.currency(price)}
              </Text>
              {frequency ? (
                <Text
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "12px", md: "16px" },
                    color: "#9CA3AF",
                  }}
                >
                  /{frequency}
                </Text>
              ) : null}
            </Stack>
          ) : null}
        </Stack>

        <Box sx={{ borderTop: "1px solid #E2E8F0", pt: { xs: "12px", md: 3 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Text sx={{ fontSize: "14px", color: "#4B5563" }}>
              {footerText}
            </Text>
            <Box
              component="button"
              type="button"
              onClick={onManageClick}
              sx={{
                border: 0,
                bgcolor: "transparent",
                p: 0,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Text
                sx={{ fontWeight: 600, fontSize: "15px", color: "#3B82F6" }}
              >
                Manage Subscription
              </Text>
              <Box
                component="img"
                src="/images/profile-v2/external-link.svg"
                alt=""
                sx={{ width: 14, height: 14 }}
              />
            </Box>
          </Stack>
        </Box>

        {investMore ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ pt: 0.5 }}
          >
            <Stack>
              <Text sx={{ fontSize: "12px", color: "#9CA3AF" }}>
                Min. Investment Amount
              </Text>
              <Text sx={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>
                {formatHelper.currency(investMore.amount)}
              </Text>
            </Stack>
            <Box
              component="a"
              href={investMore.href}
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#3B82F6",
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              Invest More
            </Box>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

export default PlanDetailCard;
